import axios from 'axios'
import { buildTenantApiPath } from '@/utils/http'
import { defineStore } from 'pinia'
import { useAuthStore } from '@/stores/auth'
import type { SharedAppEntry } from '@shared/apps/manifest'

export interface TenantAppRecord {
  id: number
  tenant_id: number
  app_key: string
  status: 'active' | 'inactive' | string
  is_trial?: boolean
  start_at?: string | null
  end_at?: string | null
  approval_status?: string | null
  meta?: Record<string, unknown> | null
}

export type TenantAppResponse =
  | {
      data?: TenantAppRecord[]
    }
  | TenantAppRecord[]

function normalizeResponse(payload: TenantAppResponse | undefined): TenantAppRecord[] {
  if (!payload) return []
  if (Array.isArray(payload)) {
    return payload
  }
  if (Array.isArray(payload.data)) {
    return payload.data
  }
  return []
}

function collectStringValues(value: unknown, result = new Set<string>()): Set<string> {
  if (typeof value === 'string' && value.trim()) {
    result.add(value.trim())
    return result
  }
  if (Array.isArray(value)) {
    value.forEach((item) => collectStringValues(item, result))
    return result
  }
  if (value && typeof value === 'object') {
    Object.values(value as Record<string, unknown>).forEach((item) => collectStringValues(item, result))
  }
  return result
}

function collectEnabledFeatureKeys(apps: TenantAppRecord[]): Set<string> {
  const auth = useAuthStore()
  const values = new Set<string>()
  collectStringValues(auth.profile?.tenant_features, values)
  apps
    .filter((app) => app.status === 'active')
    .forEach((app) => collectStringValues(app.meta, values))
  return values
}

export const useTenantAppStore = defineStore('tenant-apps', {
  state: () => ({
    tenantSlug: '',
    apps: [] as TenantAppRecord[],
    loading: false
  }),
  getters: {
    enabledAppKeys: (state) =>
      state.apps
        .filter((app) => app.status === 'active')
        .map((app) => app.app_key),
    enabledFeatureKeys: (state) => Array.from(collectEnabledFeatureKeys(state.apps)),
    isAppEnabled: (state) => (appKey?: string) => {
      if (!appKey) return true
      return state.apps.some((app) => app.app_key === appKey && app.status === 'active')
    },
    isFeatureEnabled: (state) => (feature?: string | string[]) => {
      const required = Array.isArray(feature) ? feature.filter(Boolean) : feature ? [feature] : []
      if (required.length === 0) return true
      const values = collectEnabledFeatureKeys(state.apps)
      if (values.size === 0) return true
      return required.some((item) => values.has(item))
    },
    isFeatureOrCapabilityEnabled: (state) => (entry: SharedAppEntry) => {
      const auth = useAuthStore()
      const featureKeys = collectEnabledFeatureKeys(state.apps)
      const requiredFeatures = entry.tenantFeatures ?? []
      const requiredCapabilities = entry.capabilities ?? []
      if (requiredFeatures.length === 0 && requiredCapabilities.length === 0) return true
      const featureMatched = requiredFeatures.length === 0 || featureKeys.size === 0 || requiredFeatures.some((feature) => featureKeys.has(feature))
      const capabilityMatched = auth.hasCapability(requiredCapabilities)
      return featureMatched || capabilityMatched
    }
  },
  actions: {
    async ensureApps(slug?: string, force = false): Promise<boolean> {
      const normalized = (slug ?? '').trim()

      if (!normalized) {
        this.reset()
        return true
      }

      if (!force && this.tenantSlug === normalized && this.apps.length > 0) {
        return true
      }

      this.loading = true
      try {
        const { data } = await axios.get<TenantAppResponse>(buildTenantApiPath(normalized, 'apps'), {
          params: { per_page: 200 }
        })
        this.apps = normalizeResponse(data)
        this.tenantSlug = normalized
        return true
      } catch (error) {
        console.warn('[tenant-apps] 加载学校应用列表失败', error)
        this.apps = []
        return false
      } finally {
        this.loading = false
      }
    },
    reset(): void {
      this.apps = []
      this.tenantSlug = ''
      this.loading = false
    }
  }
})
